"use client";

import { toAssetImages } from "src/lib/utils";
import svgPaths from "../header/dathacons/svg-bvmy9onrap";

export type ServicesSectionDathaconsIcon = "architecture" | "project" | "interior";

export interface ServicesSectionDathaconsService {
  title: string;
  description: string;
  icon: ServicesSectionDathaconsIcon;
}

export interface ServicesSectionDathaconsProps {
  sectionId?: string;
  backgroundImageSrc: string;
  overline: string;
  heading: string;
  services: ServicesSectionDathaconsService[];
}

function ServiceIconArchitecture() {
  return (
    <div className="relative shrink-0 size-[72px]" data-name="fi_15885479">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 72 72"
      >
        <g id="fi_15885479">
          <path d={svgPaths.p3a4eb900} fill="var(--fill-0, #4A4A4A)" />
        </g>
      </svg>
    </div>
  );
}

function Group7() {
  return (
    <div className="absolute inset-[0_0.37%]" data-name="Group">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 71.4679 72"
      >
        <g id="Group">
          <path d={svgPaths.p2e3a9e00} fill="var(--fill-0, #4A4A4A)" />
          <path d={svgPaths.p1bad70} fill="var(--fill-0, #4A4A4A)" />
          <path d={svgPaths.p17e5e200} fill="var(--fill-0, #4A4A4A)" />
          <path d={svgPaths.pf4e6c0} fill="var(--fill-0, #4A4A4A)" />
        </g>
      </svg>
    </div>
  );
}

function Group6() {
  return (
    <div className="absolute contents inset-[0_0.37%]" data-name="Group">
      <Group7 />
    </div>
  );
}

function ServiceIconProject() {
  return (
    <div className="overflow-clip relative shrink-0 size-[72px]" data-name="fi_2593258">
      <Group6 />
    </div>
  );
}

function ServiceIconInterior() {
  return (
    <div className="relative shrink-0 size-[72px]" data-name="fi_15885500">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 72 72"
      >
        <g id="fi_15885500">
          <path d={svgPaths.p1be82c00} fill="var(--fill-0, #4A4A4A)" />
        </g>
      </svg>
    </div>
  );
}

function ServiceIcon({ icon }: { icon: ServicesSectionDathaconsIcon }) {
  switch (icon) {
    case "architecture":
      return <ServiceIconArchitecture />;
    case "project":
      return <ServiceIconProject />;
    case "interior":
      return <ServiceIconInterior />;
    default:
      return <ServiceIconArchitecture />;
  }
}

function ServiceRow({ title, description, icon }: ServicesSectionDathaconsService) {
  return (
    <div className="relative shrink-0 w-full" data-name="Service row">
      <div className="content-stretch flex gap-[20px] items-start p-[30px] relative w-full max-md:p-5 max-md:gap-4">
        <ServiceIcon icon={icon} />
        <div
          className="content-stretch flex flex-[1_0_0] flex-col gap-[6px] items-start leading-[24px] min-h-px min-w-px not-italic relative text-[#303030]"
          data-name="Service details"
        >
          <p className="font-bold relative shrink-0 text-[16px] w-full">{title}</p>
          <p className="font-normal relative shrink-0 text-[15px] w-full">{description}</p>
        </div>
      </div>
    </div>
  );
}

export default function ServicesSectionDathacons({
  sectionId = "services",
  backgroundImageSrc,
  overline,
  heading,
  services,
}: ServicesSectionDathaconsProps) {
  const bgSrc =
    typeof backgroundImageSrc === "string" && backgroundImageSrc.trim() !== ""
      ? toAssetImages(backgroundImageSrc)
      : "";
  return (
    <div
      id={sectionId}
      className="content-stretch flex flex-col min-h-[691px] h-auto items-center justify-center overflow-clip pt-[95px] pb-12 md:pb-0 relative shrink-0 w-full max-md:min-h-0 max-md:pt-12"
      data-name="Services section"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute bg-white inset-0" />
        <img
          alt=""
          className="absolute max-w-none object-cover size-full"
          src={bgSrc}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute bg-gradient-to-r from-[rgba(0,0,0,0.6)] inset-0 to-[rgba(0,0,0,0)]" />
      </div>
      <div
        className="absolute right-0 top-0 bottom-0 w-1 bg-[#ffa411] z-[1] pointer-events-none hidden md:block"
        aria-hidden
      />

      <div
        className="content-stretch flex flex-col md:flex-row items-start relative shrink-0 w-[1120px] max-w-[min(1120px,calc(100%-40px))] z-[2] gap-8 md:gap-0"
        data-name="Services container max-width 1120px"
      >
        <div
          className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full md:w-[602px] max-w-full"
          data-name="Services container"
        >
          <div
            className="content-stretch flex flex-col gap-[6px] min-h-0 md:h-[150px] items-start not-italic relative shrink-0 text-[#ffa411] w-full"
            data-name="Services title container"
          >
            <p className="font-semibold leading-[normal] relative shrink-0 text-[20px] w-full">
              {overline}
            </p>
            <p className="font-extrabold leading-[1.15] md:leading-[60px] relative shrink-0 text-[35px] md:text-[47px] w-full">
              {heading}
            </p>
          </div>

          <div
            className="bg-white content-stretch flex flex-col items-start relative shrink-0 w-full shadow-sm"
            data-name="Services details container"
          >
            {services.map((s, i) => (
              <ServiceRow key={`${s.title}-${i}`} {...s} />
            ))}
          </div>
        </div>

        <div
          className="hidden md:block h-[596px] shrink-0 w-[518px]"
          data-name="Right"
          aria-hidden
        />
      </div>
    </div>
  );
}

ServicesSectionDathacons.displayName = "ServicesSectionDathacons";
